import React, { useState, useCallback } from 'react';
import { Anchor, Text, Group, Tooltip } from '@mantine/core';
import { EntityCategorizationService } from '../../services/EntityCategorizationService';

interface TargetTypeLinkProps {
    targetType: string;
}

/**
 * Component that renders TargetType as two links:
 * 1. Main link to the entity table for that type
 * 2. "- Schema" link to the schema entity detail dialog
 */
export const TargetTypeLink: React.FC<TargetTypeLinkProps> = ({ targetType }) => {
    const [entityTablePath, setEntityTablePath] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    React.useEffect(() => {
        const resolvePath = async () => {
            if (!targetType) {
                setLoading(false);
                return;
            }

            try {
                const categorizationService = EntityCategorizationService.getInstance();

                let normalizedType = targetType;
                const lowerType = targetType.toLowerCase();

                if (lowerType === 'group') {
                    normalizedType = 'groups';
                } else if (lowerType === 'class') {
                    normalizedType = 'classes';
                }

                const path = await categorizationService.getRelativePath(normalizedType);
                setEntityTablePath(path);
            } catch (error) {
                console.error(`Error resolving path for TargetType ${targetType}:`, error);
                const lowerType = targetType.toLowerCase();
                if (lowerType === 'group' || lowerType === 'groups') {
                    setEntityTablePath('groups');
                } else if (lowerType === 'class' || lowerType === 'classes') {
                    setEntityTablePath('classes');
                } else {
                    setEntityTablePath(`other/${lowerType}`);
                }
            } finally {
                setLoading(false);
            }
        };

        resolvePath();
    }, [targetType]);

    const handleEntityTableClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (entityTablePath) {
            window.location.hash = `#${entityTablePath}`;
        }
    }, [entityTablePath]);

    const handleSchemaClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        window.location.hash = `#meta/schemaentity/${targetType}`;
    }, [targetType]);

    if (loading || !targetType) {
        return <Text>{targetType || 'N/A'}</Text>;
    }

    const entityTableTooltip = entityTablePath ? `Navigate to ${entityTablePath} table` : 'Resolving...';
    const schemaTooltip = `View schema for ${targetType}`;

    return (
        <Group gap="xs" align="center">
            <Tooltip label={entityTableTooltip}>
                <Anchor
                    href={`#${entityTablePath}`}
                    onClick={handleEntityTableClick}
                    underline="hover"
                >
                    {targetType}
                </Anchor>
            </Tooltip>
            <Tooltip label={schemaTooltip}>
                <Anchor
                    href={`#meta/schemaentity/${targetType}`}
                    onClick={handleSchemaClick}
                    underline="hover"
                    size="sm"
                    c="dimmed"
                >
                    - Schema
                </Anchor>
            </Tooltip>
        </Group>
    );
};
